import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Eye } from "lucide-react";
import { toast } from "sonner";

import {
  MeasurementOverlay,
  type MeasurementTagOption,
  type MeasurementAnnotation,
  type DraftMeasurementState,
  type MeasurementOverlayRecord,
} from "@/viewport-tools/measurement/overlay";
import {
  CalibrationOverlay,
  MEASUREMENT_INCREMENT_OPTIONS,
  MEASUREMENT_TYPES,
  TAG_TOOL_IDS,
  TOOL_IDS,
  VIEWPORT_TOOLSET_PLACEMENTS,
  VIEWPORT_VIEW_IDS,
  type TagToolId,
  type ToolType,
  type MeasurementType,
} from "@/viewport-tools";
import {
  ViewportToolsetsHeader,
  ViewportToolsetsRail,
  getViewportToolsetsForPlacement,
} from "@/viewport-tools";
import { Button } from "@/components/ui/button";
import { useDocumentViewport } from "@/features/document-viewport/context";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getDocumentMediaKind } from "@/features/document-viewport/media";
import type { DocumentViewportValue, DocumentSource } from "@/features/document-viewport/types";
import {
  DocumentViewportPageControls,
  DocumentViewportToolbar,
  DocumentViewportToolbarSection,
} from "@/features/document-viewport/toolbar";
import { getViewportContentCenter, MAX_VIEWPORT_ZOOM } from "@/features/document-viewport/utils";
import { DocumentTagEditorOverlay } from "@/features/document-intelligence/document-tag-editor-overlay";
import { useTagInstanceNavigation } from "@/features/takeoff-pilot/use-tag-instance-navigation";
import { useTakeoffViewportToolsetControllers } from "@/features/takeoff-pilot/use-takeoff-viewport-toolset-controllers";
import { useMeasurementScale } from "@/hooks/useMeasurementScale";
import { useMeasurementTextLock } from "@/hooks/useMeasurementTextLock";
import { useLoggedMutation, useRecordActionLog } from "@/hooks/use-action-log-session";
import { computeMeasurementValue, parseFeetInches, type Point } from "@/lib/geometry";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { TakeoffViewerHeader } from "@/features/takeoff-pilot/viewer-header";
import type { MeasurementBreakdownTargetRef, ViewerSource } from "@/types/takeoff-pilot";
import type { ProcessingStateWithIdle } from "@/lib/processing-states";
import type { DocumentTagOption, DocumentTagPlacementMode } from "@/viewport-tools/tags";

type DetectionRecord = {
  _id: Id<"documentIntelligenceDetections">;
  pageNumber: number;
  rawText: string;
  normalizedText: string;
  classification: "finish_tag" | "room_tag" | "door_window_tag" | "reference_noise" | "unknown";
  bbox: { x: number; y: number; width: number; height: number };
  isUserManaged?: boolean;
  createdByUserId?: Id<"users">;
};

const MIXED_MEASUREMENT_TYPE_ERROR =
  "Only one dimensional measurement type can be assigned to a tag";
const MEASUREMENT_INCREMENT_STORAGE_KEY = "takeoff-measurement-increment-snap";
const DEFAULT_INCREMENT_STEP_FEET = 1;
const VALID_MEASUREMENT_INCREMENT_STEPS = new Set(
  MEASUREMENT_INCREMENT_OPTIONS.map((option) => option.stepFeet)
);
function hexToRgba(color: string, alpha: number) {
  const normalized = color.trim();
  const shortMatch = /^#([\da-f]{3})$/i.exec(normalized);
  if (shortMatch) {
    const [r, g, b] = shortMatch[1].split("").map((channel) => parseInt(channel + channel, 16));
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }

  const longMatch = /^#([\da-f]{6})$/i.exec(normalized);
  if (!longMatch) {
    return undefined;
  }

  const int = parseInt(longMatch[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getMeasurementMutationErrorMessage(error: unknown, fallbackMessage: string) {
  const message = getUserFacingErrorMessage(error, fallbackMessage);
  const rawMessage = error instanceof Error ? error.message : message;

  if (rawMessage.includes(MIXED_MEASUREMENT_TYPE_ERROR)) {
    return "This tag already has a different measurement type. Keep LF, SQFT, and CF on separate tags.";
  }

  if (rawMessage.includes("Measurements can only be attached to selected tags")) {
    return "Choose a selected tag before saving or reassigning a measurement.";
  }

  return message;
}

function getMeasurementGeometryBounds(geometry: MeasurementOverlayRecord["geometry"]) {
  if (geometry.type === "rect") {
    return {
      minX: geometry.x,
      minY: geometry.y,
      maxX: geometry.x + geometry.width,
      maxY: geometry.y + geometry.height,
    };
  }

  const xs = geometry.vertices.map((vertex) => vertex.x);
  const ys = geometry.vertices.map((vertex) => vertex.y);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

function getRevisionPageDetectionQueryArgs(args: {
  revisionId: Id<"projectDocumentRevisions">;
  normalizedTexts: string[];
  pageNumber: number;
}) {
  if (args.normalizedTexts.length === 0) {
    return "skip" as const;
  }

  return {
    revisionId: args.revisionId,
    pageNumber: args.pageNumber,
    normalizedTexts: args.normalizedTexts,
  };
}

function getRevisionDetectionQueryArgs(args: {
  revisionId: Id<"projectDocumentRevisions">;
  normalizedTexts: string[];
}) {
  if (args.normalizedTexts.length === 0) {
    return "skip" as const;
  }

  return {
    revisionId: args.revisionId,
    normalizedTexts: args.normalizedTexts,
  };
}

export interface TakeoffViewerTag {
  tagId: Id<"takeoffPilotExtractedTags">;
  rawValue: string;
  normalizedValue: string;
  colorHex?: string;
}

export interface TakeoffResultsViewerHandle {
  focusNextInstanceForTag: (tagId: Id<"takeoffPilotExtractedTags"> | string) => void;
  focusPreviousInstanceForTag: (tagId: Id<"takeoffPilotExtractedTags"> | string) => void;
  focusMeasurementBreakdownRow: (args: {
    rowKey: string;
    direction: "previous" | "next";
    targets: MeasurementBreakdownTargetRef[];
  }) => void;
}

interface TakeoffResultsViewerProps {
  jobId: Id<"takeoffPilotJobs">;
  planDocumentId: Id<"takeoffPilotPlanDocuments"> | null;
  planRevisionId: Id<"projectDocumentRevisions">;
  planFilename: string;
  planFileUrl: string | null;
  planProjectId: Id<"projects">;
  planSourceDocumentId: Id<"projectDocuments">;
  documentIntelligenceRunId: Id<"documentIntelligenceRuns"> | null;
  tags: TakeoffViewerTag[];
  isViewOnly?: boolean;
  onNavigableTagIdsChange?: (tagIds: string[]) => void;
  viewerSources: ViewerSource[];
  selectedPlanDocumentId: string;
  onSelectPlanDocumentId: (value: string) => void;
  onClose: () => void;
  onDownloadWorkbook: () => void;
  onExportPdf: () => void;
  onDownloadPdf: () => void;
  exportStatus: ProcessingStateWithIdle;
  exportError?: string;
  isPdfExportInProgress: boolean;
  canExportPdf: boolean;
  canDownloadPdf: boolean;
  canDownload: boolean;
  bottomPanelHeight?: number;
}

function getBottomPanelPanPadding(bottomPanelHeight: number | undefined) {
  // Pan clamping is centered on the available content rect, so a bottom-only
  // overlay changes the usable bottom edge by half its height. Double the
  // overlay height here to reserve the full covered area for panning.
  return (bottomPanelHeight ?? 0) * 2 + 28;
}

function getTagPlacementMode(activeTagTool: TagToolId | null): DocumentTagPlacementMode {
  if (activeTagTool === TAG_TOOL_IDS.add) {
    return "add";
  }
  if (activeTagTool === TAG_TOOL_IDS.duplicate) {
    return "duplicate";
  }
  return null;
}

function ViewportValueBinder({
  onViewportChange,
}: {
  onViewportChange: (viewport: DocumentViewportValue) => void;
}) {
  const viewport = useDocumentViewport();

  useEffect(() => {
    onViewportChange(viewport);
  }, [onViewportChange, viewport]);

  return null;
}

function TakeoffResultsViewportToolbar({
  activePage,
  pageCount,
  showOverlays,
  overlaysAvailable,
  setShowOverlays,
}: {
  activePage: number;
  pageCount: number;
  showOverlays: boolean;
  overlaysAvailable: boolean;
  setShowOverlays: (value: boolean) => void;
}) {
  return (
    <DocumentViewportToolbar className="gap-2">
      <DocumentViewportToolbarSection>
        <Button
          type="button"
          variant={showOverlays ? "secondary" : "ghost"}
          size="sm"
          className="h-8 gap-1.5 px-2 text-[11px] font-semibold uppercase tracking-[0.12em]"
          disabled={!overlaysAvailable}
          onClick={() => setShowOverlays(!showOverlays)}
          aria-label={
            overlaysAvailable
              ? showOverlays
                ? "Hide counted tags"
                : "Show counted tags"
              : "Tag overlays unavailable for this document"
          }
        >
          <Eye className="size-3.5" />
          {overlaysAvailable ? (showOverlays ? "Tags On" : "Tags Off") : "View Only"}
        </Button>
      </DocumentViewportToolbarSection>

      <DocumentViewportPageControls activePage={activePage} pageCount={pageCount} />
    </DocumentViewportToolbar>
  );
}

export const TakeoffResultsViewer = forwardRef<
  TakeoffResultsViewerHandle,
  TakeoffResultsViewerProps
>(function TakeoffResultsViewer({
  jobId,
  planDocumentId,
  planRevisionId,
  planFilename,
  planFileUrl,
  planProjectId,
  planSourceDocumentId,
  documentIntelligenceRunId: _documentIntelligenceRunId,
  tags,
  isViewOnly = false,
  onNavigableTagIdsChange,
  viewerSources,
  selectedPlanDocumentId,
  onSelectPlanDocumentId,
  onClose,
  onDownloadWorkbook,
  onExportPdf,
  onDownloadPdf,
  exportStatus,
  exportError,
  isPdfExportInProgress,
  canExportPdf,
  canDownloadPdf,
  canDownload,
  bottomPanelHeight,
}, ref) {
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [showOverlays, setShowOverlays] = useState(true);
  const [activeToolsetId, setActiveToolsetId] = useState<
    "scale" | "measurement" | "tags" | null
  >(null);
  const [activeTool, setActiveTool] = useState<ToolType | null>(null);
  const [activeMeasurementType, setActiveMeasurementType] = useState<MeasurementType>(
    MEASUREMENT_TYPES.SQFT
  );
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [incrementSnapEnabled, setIncrementSnapEnabled] = useState(false);
  const [incrementStepFeet, setIncrementStepFeet] = useState(DEFAULT_INCREMENT_STEP_FEET);
  const [incrementSnapStorageLoaded, setIncrementSnapStorageLoaded] = useState(false);
  const [heightValue, setHeightValue] = useState("");
  const [draftMeasurement, setDraftMeasurement] = useState<DraftMeasurementState | null>(null);
  const [isSavingDraftMeasurement, setIsSavingDraftMeasurement] = useState(false);
  const [isReassigningMeasurementId, setIsReassigningMeasurementId] =
    useState<Id<"takeoffPilotMeasurements"> | null>(null);
  const [isDeletingMeasurementId, setIsDeletingMeasurementId] =
    useState<Id<"takeoffPilotMeasurements"> | null>(null);
  const [selectedMeasurementIds, setSelectedMeasurementIds] = useState<
    Id<"takeoffPilotMeasurements">[]
  >([]);
  const [activeMeasurementId, setActiveMeasurementId] =
    useState<Id<"takeoffPilotMeasurements"> | null>(null);
  const { textLocked, toggleTextLock, textFontSize, setTextFontSize } =
    useMeasurementTextLock({
      jobId,
      planDocumentId: planDocumentId ?? undefined,
      planRevisionId: planRevisionId ?? undefined,
    });
  const [copiedMeasurementIds, setCopiedMeasurementIds] = useState<
    Id<"takeoffPilotMeasurements">[]
  >([]);
  const [activeTagTool, setActiveTagTool] = useState<TagToolId | null>(null);
  const [selectedAddTagOptionId, setSelectedAddTagOptionId] = useState<string | null>(null);
  const [lastUsedMeasurementTagId, setLastUsedMeasurementTagId] =
    useState<Id<"takeoffPilotExtractedTags"> | null>(null);
  const [activePageAspectRatio, setActivePageAspectRatio] = useState<number | undefined>(undefined);
  const [activePagePixelMetrics, setActivePagePixelMetrics] = useState({
    width: 0,
    height: 0,
  });
  const viewportRef = useRef<DocumentViewportValue | null>(null);
  const focusedMeasurementIdByBreakdownRowRef = useRef<Record<string, string>>({});
  const pendingDetectionFocusRef = useRef<DetectionRecord | null>(null);
  const pendingMeasurementFocusRef = useRef<MeasurementBreakdownTargetRef | null>(null);

  const measurementScale = useMeasurementScale({
    projectId: planProjectId,
    documentId: planSourceDocumentId,
    revisionId: planRevisionId,
    pageNumber: activePage,
    pageAspectRatio: activePageAspectRatio,
  });
  const pointerOwner =
    !isViewOnly &&
    (measurementScale.isCalibrationMode || activeTool || activeTagTool || activeToolsetId === "tags")
      ? "tool"
      : "viewport";
  const currentHeightFeet = useMemo(() => {
    const parsed = parseFeetInches(heightValue);
    return parsed && parsed > 0 ? parsed : undefined;
  }, [heightValue]);
  const handleMeasurementToolChange = useCallback((tool: ToolType | null) => {
    setActiveTool(tool);
    if (tool) {
      setActiveTagTool(null);
    }
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
  }, []);
  const handleMeasurementTypeChange = useCallback((type: MeasurementType) => {
    setActiveMeasurementType(type);
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
  }, []);
  const railTagOptions = useMemo(
    () => tags.map((tag) => ({ id: String(tag.tagId), label: tag.rawValue })),
    [tags]
  );
  const handleTagsToolSelect = useCallback((toolId: TagToolId | null) => {
    setActiveTagTool(toolId);
    if (toolId) {
      setActiveTool(null);
      setDraftMeasurement(null);
      setSelectedMeasurementIds([]);
      setActiveMeasurementId(null);
      setActiveToolsetId("tags");
    }
  }, []);
  const overlaysEnabled = !isViewOnly && showOverlays;

  useEffect(() => {
    if (railTagOptions.length === 0) {
      setSelectedAddTagOptionId(null);
      return;
    }

    if (
      selectedAddTagOptionId &&
      railTagOptions.some((option) => option.id === selectedAddTagOptionId)
    ) {
      return;
    }

    setSelectedAddTagOptionId(railTagOptions[0].id);
  }, [railTagOptions, selectedAddTagOptionId]);

  const { enabledToolsets: railToolsets, controllers: railControllers } =
    useTakeoffViewportToolsetControllers({
      measurementScale,
      activeToolsetId,
      onActiveToolsetChange: setActiveToolsetId,
      activeTool,
      activeMeasurementType,
      onMeasurementToolChange: handleMeasurementToolChange,
      onMeasurementTypeChange: handleMeasurementTypeChange,
      snapEnabled,
      onSnapToggle: () => setSnapEnabled(!snapEnabled),
      incrementSnapEnabled,
      onIncrementSnapToggle: () => setIncrementSnapEnabled((current) => !current),
      incrementStepFeet,
      onIncrementStepChange: setIncrementStepFeet,
      heightValue,
      onHeightChange: setHeightValue,
      textLocked,
      onTextLockToggle: toggleTextLock,
      textFontSize,
      onTextFontSizeChange: setTextFontSize,
      activeTagTool,
      tagOptions: railTagOptions,
      selectedTagOptionId: selectedAddTagOptionId,
      onSelectedTagOptionChange: setSelectedAddTagOptionId,
      showOverlays: overlaysEnabled,
      onTagsToolSelect: handleTagsToolSelect,
      pagePixelWidth: activePagePixelMetrics.width,
      pagePixelHeight: activePagePixelMetrics.height,
    });
  const headerToolsets = useMemo(
    () =>
      getViewportToolsetsForPlacement(
        VIEWPORT_VIEW_IDS.takeoffPilot,
        VIEWPORT_TOOLSET_PLACEMENTS.topToolbar
      ),
    []
  );
  const recordActionLog = useRecordActionLog();
  const createMeasurement = useLoggedMutation(api.takeoffPilot.measurements.create);
  const updateMeasurement = useLoggedMutation(api.takeoffPilot.measurements.update);
  const removeMeasurements = useLoggedMutation(api.takeoffPilot.measurements.removeMany);
  const reassignMeasurements = useLoggedMutation(api.takeoffPilot.measurements.reassignTagMany);
  const duplicateMeasurements = useLoggedMutation(api.takeoffPilot.measurements.duplicateMany);
  const applyMeasurementLeaders = useLoggedMutation(api.takeoffPilot.measurements.applyLeaderMany);

  useEffect(() => {
    if (typeof window === "undefined") {
      setIncrementSnapStorageLoaded(true);
      return;
    }

    try {
      const stored = window.localStorage.getItem(MEASUREMENT_INCREMENT_STORAGE_KEY);
      if (!stored) {
        return;
      }

      const parsed = JSON.parse(stored) as { enabled?: unknown; stepFeet?: unknown };
      if (typeof parsed.enabled === "boolean") {
        setIncrementSnapEnabled(parsed.enabled);
      }
      if (
        typeof parsed.stepFeet === "number" &&
        VALID_MEASUREMENT_INCREMENT_STEPS.has(parsed.stepFeet)
      ) {
        setIncrementStepFeet(parsed.stepFeet);
      }
    } catch {
      // Ignore invalid persisted values and keep defaults.
    } finally {
      setIncrementSnapStorageLoaded(true);
    }
  }, []);

  useEffect(() => {
    if (!incrementSnapStorageLoaded || typeof window === "undefined") {
      return;
    }

    window.localStorage.setItem(
      MEASUREMENT_INCREMENT_STORAGE_KEY,
      JSON.stringify({
        enabled: incrementSnapEnabled,
        stepFeet: incrementStepFeet,
      })
    );
  }, [incrementSnapEnabled, incrementSnapStorageLoaded, incrementStepFeet]);

  const normalizedTexts = useMemo(
    () =>
      Array.from(new Set(tags.map((tag) => tag.normalizedValue).filter((value) => value.length > 0))),
    [tags]
  );
  const colorByNormalizedText = useMemo(
    () =>
      new Map(
        tags
          .filter((tag) => tag.colorHex && tag.normalizedValue.length > 0)
          .map((tag) => [tag.normalizedValue, tag.colorHex!] as const)
      ),
    [tags]
  );
  const colorByRawText = useMemo(
    () =>
      new Map(
        tags
          .filter((tag) => tag.colorHex && tag.rawValue.trim().length > 0)
          .map((tag) => [tag.rawValue.trim(), tag.colorHex!] as const)
      ),
    [tags]
  );
  const colorByTagId = useMemo(
    () => new Map(tags.map((tag) => [String(tag.tagId), tag.colorHex ?? "#0EA5E9"])),
    [tags]
  );
  const labelByTagId = useMemo(
    () => new Map(tags.map((tag) => [String(tag.tagId), tag.rawValue])),
    [tags]
  );
  const measurementTags = useMemo<MeasurementTagOption[]>(
    () =>
      tags.map((tag) => ({
        id: tag.tagId,
        label: tag.rawValue,
      })),
    [tags]
  );
  const measurementTagIdSet = useMemo(
    () => new Set(measurementTags.map((tag) => String(tag.id))),
    [measurementTags]
  );
  const measurementTagIds = useMemo(
    () => measurementTags.map((tag) => tag.id),
    [measurementTags]
  );

  const { data: pageDetections, isPending } = useQuery(
    convexQuery(
      api.documentIntelligence.tags.listByRevisionPage,
      getRevisionPageDetectionQueryArgs({
        revisionId: planRevisionId,
        pageNumber: activePage,
        normalizedTexts,
      })
    )
  );
  const { data: allDetections } = useQuery(
    convexQuery(
      api.documentIntelligence.tags.listByRevision,
      getRevisionDetectionQueryArgs({
        revisionId: planRevisionId,
        normalizedTexts,
      })
    )
  );

  const { data: pageMeasurements = [] } = useQuery(
    convexQuery(
      api.takeoffPilot.measurements.listByJobPage,
      planDocumentId && !isViewOnly
        ? {
            jobId,
            planDocumentId,
            pageNumber: activePage,
            tagIds: measurementTagIds,
          }
        : "skip"
    )
  );
  const activePageDetections = (pageDetections?.activeDetections ?? []) as DetectionRecord[];
  const archivedPageDetections = (pageDetections?.archivedDetections ?? []) as DetectionRecord[];
  const allActiveDetections = (allDetections?.activeDetections ?? []) as DetectionRecord[];
  const tagPlacementOptions = useMemo<DocumentTagOption[]>(() => {
    const detections = [...activePageDetections, ...allActiveDetections];
    return tags.map((tag) => {
      const matchingDetection = detections.find(
        (detection) =>
          detection.normalizedText === tag.normalizedValue ||
          detection.rawText.trim() === tag.rawValue.trim()
      );

      return {
        id: String(tag.tagId),
        label: tag.rawValue,
        rawText: tag.rawValue,
        normalizedText: tag.normalizedValue,
        color: tag.colorHex ?? colorByTagId.get(String(tag.tagId)),
        ...(matchingDetection
          ? {
              classification: matchingDetection.classification,
              bbox: matchingDetection.bbox,
            }
          : {}),
      };
    });
  }, [activePageDetections, allActiveDetections, colorByTagId, tags]);
  const tagPlacementMode = getTagPlacementMode(activeTagTool);
  const getDetectionColor = (detection: DetectionRecord) =>
    colorByRawText.get(detection.rawText.trim()) ??
    colorByNormalizedText.get(detection.normalizedText) ??
    "#0EA5E9";

  const focusDetection = useCallback((detection: DetectionRecord) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const contentCenter = getViewportContentCenter(
      viewport.workspaceSize,
      viewport.viewportPadding
    );
    const pageBounds = viewport.transforms.pageRectToScreenRect({
      x: 0,
      y: 0,
      width: 1,
      height: 1,
    });
    const center = {
      x: detection.bbox.x + detection.bbox.width / 2,
      y: detection.bbox.y + detection.bbox.height / 2,
    };
    const baseWidth = pageBounds.width / viewport.camera.zoom;
    const baseHeight = pageBounds.height / viewport.camera.zoom;

    viewport.requestZoomAt(MAX_VIEWPORT_ZOOM, contentCenter);
    viewport.setPan({
      x: baseWidth * MAX_VIEWPORT_ZOOM * (0.5 - center.x),
      y: baseHeight * MAX_VIEWPORT_ZOOM * (0.5 - center.y),
    });
  }, []);
  const focusMeasurementGeometry = useCallback((geometry: MeasurementOverlayRecord["geometry"]) => {
    const viewport = viewportRef.current;
    if (!viewport) {
      return;
    }

    const bounds = getMeasurementGeometryBounds(geometry);
    const center = {
      x: (bounds.minX + bounds.maxX) / 2,
      y: (bounds.minY + bounds.maxY) / 2,
    };
    const span = Math.max(bounds.maxX - bounds.minX, bounds.maxY - bounds.minY, 0.02);
    const targetZoom = Math.min(MAX_VIEWPORT_ZOOM, Math.max(3, 0.45 / span));
    const contentCenter = getViewportContentCenter(
      viewport.workspaceSize,
      viewport.viewportPadding
    );
    const pageBounds = viewport.transforms.pageRectToScreenRect({
      x: 0,
      y: 0,
      width: 1,
      height: 1,
    });
    const baseWidth = pageBounds.width / viewport.camera.zoom;
    const baseHeight = pageBounds.height / viewport.camera.zoom;

    viewport.requestZoomAt(targetZoom, contentCenter);
    viewport.setPan({
      x: baseWidth * targetZoom * (0.5 - center.x),
      y: baseHeight * targetZoom * (0.5 - center.y),
    });
  }, []);
  const handleViewportChange = useCallback((viewport: DocumentViewportValue) => {
    viewportRef.current = viewport;
    const nextAspectRatio = viewport.activePageMetrics?.aspectRatio;
    const nextWidth = viewport.activePageMetrics?.width ?? 0;
    const nextHeight = viewport.activePageMetrics?.height ?? 0;
    const normalizedAspectRatio =
      Number.isFinite(nextAspectRatio) && (nextAspectRatio as number) > 0
        ? (nextAspectRatio as number)
        : undefined;

    setActivePagePixelMetrics((current) =>
      current.width === nextWidth && current.height === nextHeight
        ? current
        : {
            width: nextWidth,
            height: nextHeight,
          }
    );
    setActivePageAspectRatio((current) =>
      current === normalizedAspectRatio ? current : normalizedAspectRatio
    );
  }, []);

  const {
    focusNextInstanceForTag,
    focusPreviousInstanceForTag,
    highlightedDetectionId,
    navigableTagIds,
  } = useTagInstanceNavigation({
    tags,
    activeDetections: allActiveDetections,
    enabled: overlaysEnabled,
    navigationScopeKey: `${String(planDocumentId ?? "none")}:${String(planRevisionId)}`,
    onFocusDetection: (detection) => {
      if (detection.pageNumber !== activePage) {
        pendingDetectionFocusRef.current = detection;
        viewportRef.current?.goToPage(detection.pageNumber);
        return;
      }

      pendingDetectionFocusRef.current = null;
      focusDetection(detection);
    },
  });

  const focusMeasurementTarget = useCallback((target: MeasurementBreakdownTargetRef) => {
    const pageRows = pageMeasurements as MeasurementOverlayRecord[];
    const isCurrentSource = String(planDocumentId ?? "") === String(target.planDocumentId);
    const isCurrentPage = activePage === target.pageNumber;

    if (isCurrentSource && isCurrentPage) {
      const measurement = pageRows.find(
        (row) => String(row._id) === String(target.measurementId)
      );
      if (measurement) {
        pendingMeasurementFocusRef.current = null;
        setSelectedMeasurementIds([measurement._id]);
        setActiveMeasurementId(measurement._id);
        focusMeasurementGeometry(measurement.geometry);
        return;
      }
    }

    pendingMeasurementFocusRef.current = target;
    if (!isCurrentSource) {
      onSelectPlanDocumentId(String(target.planDocumentId));
      return;
    }
    if (!isCurrentPage) {
      viewportRef.current?.goToPage(target.pageNumber);
    }
  }, [activePage, focusMeasurementGeometry, onSelectPlanDocumentId, pageMeasurements, planDocumentId]);

  const focusMeasurementBreakdownRow = useCallback((args: {
    rowKey: string;
    direction: "previous" | "next";
    targets: MeasurementBreakdownTargetRef[];
  }) => {
    if (args.targets.length === 0) {
      return;
    }

    const direction = args.direction === "previous" ? -1 : 1;
    const currentMeasurementId = focusedMeasurementIdByBreakdownRowRef.current[args.rowKey];
    const currentIndex = currentMeasurementId
      ? args.targets.findIndex(
          (target) => String(target.measurementId) === currentMeasurementId
        )
      : -1;
    const baseIndex = currentIndex === -1 ? (direction === 1 ? -1 : 0) : currentIndex;
    const nextTarget =
      args.targets[(baseIndex + direction + args.targets.length) % args.targets.length];
    if (!nextTarget) {
      return;
    }

    focusedMeasurementIdByBreakdownRowRef.current = {
      ...focusedMeasurementIdByBreakdownRowRef.current,
      [args.rowKey]: String(nextTarget.measurementId),
    };
    focusMeasurementTarget(nextTarget);
  }, [focusMeasurementTarget]);

  useImperativeHandle(ref, () => ({
    focusNextInstanceForTag,
    focusPreviousInstanceForTag,
    focusMeasurementBreakdownRow,
  }), [focusMeasurementBreakdownRow, focusNextInstanceForTag, focusPreviousInstanceForTag]);

  useEffect(() => {
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
    setActiveTool(null);
    setActiveTagTool(null);
    setCopiedMeasurementIds([]);
    setActivePageAspectRatio(undefined);
    setActiveToolsetId(null);
  }, [activePage]);

  useEffect(() => {
    setActivePage(1);
    setDraftMeasurement(null);
    setSelectedMeasurementIds([]);
    setActiveMeasurementId(null);
    setActiveTool(null);
    setActiveTagTool(null);
    setCopiedMeasurementIds([]);
    setActivePageAspectRatio(undefined);
    setActiveToolsetId(null);
    pendingDetectionFocusRef.current = null;
  }, [planDocumentId, planRevisionId]);

  useEffect(() => {
    const pendingDetection = pendingDetectionFocusRef.current;
    if (!pendingDetection || pendingDetection.pageNumber !== activePage) {
      return;
    }

    focusDetection(pendingDetection);
    pendingDetectionFocusRef.current = null;
  }, [activePage, focusDetection]);

  useEffect(() => {
    onNavigableTagIdsChange?.(navigableTagIds);
  }, [navigableTagIds, onNavigableTagIdsChange]);

  useEffect(() => {
    const pendingTarget = pendingMeasurementFocusRef.current;
    if (!pendingTarget) {
      return;
    }

    if (String(planDocumentId ?? "") !== String(pendingTarget.planDocumentId)) {
      return;
    }

    if (activePage !== pendingTarget.pageNumber) {
      viewportRef.current?.goToPage(pendingTarget.pageNumber);
      return;
    }

    const measurement = (pageMeasurements as MeasurementOverlayRecord[]).find(
      (row) => String(row._id) === String(pendingTarget.measurementId)
    );
    if (!measurement) {
      return;
    }

    setSelectedMeasurementIds([measurement._id]);
    setActiveMeasurementId(measurement._id);
    focusMeasurementGeometry(measurement.geometry);
    pendingMeasurementFocusRef.current = null;
  }, [activePage, focusMeasurementGeometry, pageMeasurements, planDocumentId]);

  useEffect(() => {
    if (lastUsedMeasurementTagId && !measurementTagIdSet.has(String(lastUsedMeasurementTagId))) {
      setLastUsedMeasurementTagId(null);
    }
  }, [lastUsedMeasurementTagId, measurementTagIdSet]);

  useEffect(() => {
    setDraftMeasurement((current) => {
      if (!current?.tagId) {
        return current;
      }
      return measurementTagIdSet.has(String(current.tagId))
        ? current
        : { ...current, tagId: undefined };
    });
  }, [measurementTagIdSet]);

  useEffect(() => {
    if (overlaysEnabled) {
      return;
    }
    setActiveTagTool(null);
    setActiveToolsetId((current) => (current === "tags" ? null : current));
  }, [overlaysEnabled]);

  useEffect(() => {
    const activeScale = measurementScale.scale;
    if (!activeScale) {
      return;
    }
    setDraftMeasurement((current) => {
      if (
        !current ||
        !(
          current.measurementType === MEASUREMENT_TYPES.CF ||
          ((current.tool === TOOL_IDS.line || current.tool === TOOL_IDS.polyline) &&
            current.measurementType === MEASUREMENT_TYPES.SQFT)
        )
      ) {
        return current;
      }
      if (current.height === currentHeightFeet) {
        return current;
      }
      const nextValue = currentHeightFeet
        ? computeMeasurementValue(
            current.tool,
            current.measurementType,
            current.geometry,
            currentHeightFeet,
            activeScale.pixelsPerFoot,
            activeScale.pageAspectRatio
          )
        : 0;
      return {
        ...current,
        height: currentHeightFeet,
        value: nextValue,
      };
    });
  }, [currentHeightFeet, measurementScale.scale]);

  const source = useMemo<DocumentSource | null>(() => {
    if (!planFileUrl) {
      return null;
    }

    return {
      revisionId: String(planRevisionId),
      fileUrl: planFileUrl,
      filename: planFilename,
      mediaKind: getDocumentMediaKind(planFilename, "application/pdf"),
      mimeType: "application/pdf",
    };
  }, [planFileUrl, planFilename, planRevisionId]);

  if (!source) {
    return (
      <div className="flex h-[720px] items-center justify-center rounded-xl border bg-muted/20 text-sm text-muted-foreground">
        Unable to load the selected plan document.
      </div>
    );
  }

  const handleDraftMeasurementChange = (draft: DraftMeasurementState | null) => {
    setDraftMeasurement(
      draft
        ? {
            ...draft,
            tagId:
              draft.tagId ??
              (lastUsedMeasurementTagId && measurementTagIdSet.has(String(lastUsedMeasurementTagId))
                ? lastUsedMeasurementTagId
                : undefined),
          }
        : null
    );
  };

  const handleSaveDraftMeasurement = async () => {
    if (!draftMeasurement || isSavingDraftMeasurement) return;
    if (!planDocumentId) {
      toast.error("Measurements are unavailable for this document");
      return;
    }
    if (!draftMeasurement.tagId) {
      toast.error("Choose a tag for the measurement");
      return;
    }
    try {
      setIsSavingDraftMeasurement(true);
      await createMeasurement({
        jobId,
        planDocumentId,
        pageNumber: activePage,
        tagId: draftMeasurement.tagId,
        tool: draftMeasurement.tool,
        measurementType: draftMeasurement.measurementType,
        geometry: draftMeasurement.geometry,
        annotation: draftMeasurement.annotation,
        height: draftMeasurement.height,
        value: draftMeasurement.value,
      });
      setLastUsedMeasurementTagId(draftMeasurement.tagId);
      setDraftMeasurement(null);
      setSelectedMeasurementIds([]);
      setActiveMeasurementId(null);
      toast.success("Measurement saved");
    } catch (error) {
      console.error(error);
      toast.error(getMeasurementMutationErrorMessage(error, "Failed to save measurement"));
      throw error;
    } finally {
      setIsSavingDraftMeasurement(false);
    }
  };

  const handleUpdateMeasurement = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: DraftMeasurementState["geometry"],
    height: number | undefined,
    value: number
  ) => {
    try {
      await updateMeasurement({
        measurementId,
        geometry,
        height,
        value,
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleUpdateMeasurementAnnotation = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => {
    try {
      await updateMeasurement({
        measurementId,
        annotation,
      });
    } catch (error) {
      console.error(error);
      throw error;
    }
  };

  const handleReassignMeasurement = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    tagId: Id<"takeoffPilotExtractedTags">
  ) => {
    return handleReassignMeasurements([measurementId], tagId);
  };

  const handleDeleteMeasurement = async (measurementId: Id<"takeoffPilotMeasurements">) => {
    await handleDeleteMeasurements([measurementId]);
  };

  const handleSelectMeasurement = (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => {
    if (!measurementId) {
      setSelectedMeasurementIds([]);
      setActiveMeasurementId(null);
      return;
    }
    if (!options?.shiftKey) {
      setSelectedMeasurementIds([measurementId]);
      setActiveMeasurementId(measurementId);
      return;
    }
    setSelectedMeasurementIds((current) => {
      if (current.includes(measurementId)) {
        const next = current.filter((id) => id !== measurementId);
        setActiveMeasurementId((active) => (active === measurementId ? (next[0] ?? null) : active));
        return next;
      }
      setActiveMeasurementId(measurementId);
      return [...current, measurementId];
    });
  };

  const handleCopyMeasurement = (measurementId: Id<"takeoffPilotMeasurements">) => {
    const measurement =
      (pageMeasurements as MeasurementOverlayRecord[]).find(
        (item) => item._id === measurementId
      ) ?? null;
    if (!measurement) {
      return;
    }
    setCopiedMeasurementIds([measurement._id]);
  };

  const handleCopyMeasurements = (measurementIds: Id<"takeoffPilotMeasurements">[]) => {
    const availableMeasurementIds = new Set(
      (pageMeasurements as MeasurementOverlayRecord[]).map((measurement) =>
        String(measurement._id)
      )
    );
    const nextCopiedIds = measurementIds.filter((measurementId) =>
      availableMeasurementIds.has(String(measurementId))
    );
    if (nextCopiedIds.length > 0) {
      setCopiedMeasurementIds(nextCopiedIds);
    }
  };

  const handlePasteMeasurement = async (targetPoint?: Point) => {
    if (copiedMeasurementIds.length === 0) {
      return;
    }

    try {
      const availableMeasurementIds = new Set(
        (pageMeasurements as MeasurementOverlayRecord[]).map((measurement) =>
          String(measurement._id)
        )
      );
      const validCopiedMeasurementIds = copiedMeasurementIds.filter((measurementId) =>
        availableMeasurementIds.has(String(measurementId))
      );
      if (validCopiedMeasurementIds.length !== copiedMeasurementIds.length) {
        setCopiedMeasurementIds(validCopiedMeasurementIds);
      }
      if (validCopiedMeasurementIds.length === 0) {
        toast.error("No copied measurements are available to paste");
        return;
      }

      const result = await duplicateMeasurements({
        measurementIds: validCopiedMeasurementIds,
        targetPoint,
      });
      const createdIds = result?.createdMeasurementIds ?? [];
      if (createdIds.length === 0) {
        toast.error("No copied measurements are available to paste");
        return;
      }
      setSelectedMeasurementIds(createdIds);
      setActiveMeasurementId(createdIds[0] ?? null);
      toast.success(
        createdIds.length === 1
          ? "Measurement duplicated"
          : `${createdIds.length} measurements duplicated`
      );
    } catch (error) {
      console.error(error);
      toast.error(getMeasurementMutationErrorMessage(error, "Failed to duplicate measurement"));
    }
  };

  const handleDuplicateMeasurement = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    targetPoint?: Point
  ) => {
    await handleDuplicateMeasurements([measurementId], targetPoint);
  };

  const handleDuplicateMeasurements = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    targetPoint?: Point
  ) => {
    if (measurementIds.length === 0) {
      return;
    }
    try {
      const sourceMeasurements = (pageMeasurements as MeasurementOverlayRecord[]).filter((item) =>
        measurementIds.includes(item._id)
      );
      const result = await duplicateMeasurements({ measurementIds, targetPoint });
      const createdIds = result?.createdMeasurementIds ?? [];
      setSelectedMeasurementIds(createdIds);
      setActiveMeasurementId(createdIds[0] ?? null);
      if (sourceMeasurements.length > 0) {
        setCopiedMeasurementIds(sourceMeasurements.map((measurement) => measurement._id));
      }
      toast.success(
        measurementIds.length === 1 ? "Measurement duplicated" : `${measurementIds.length} measurements duplicated`
      );
    } catch (error) {
      console.error(error);
      toast.error(getMeasurementMutationErrorMessage(error, "Failed to duplicate measurement"));
    }
  };

  const handleDeleteMeasurements = async (measurementIds: Id<"takeoffPilotMeasurements">[]) => {
    if (measurementIds.length === 0) {
      return;
    }
    try {
      if (measurementIds.length === 1) {
        setIsDeletingMeasurementId(measurementIds[0]);
      }
      const result = await removeMeasurements({ measurementIds });
      const deletedCount = result?.deletedCount ?? 0;
      if (deletedCount > 0) {
        const deletedSet = new Set(measurementIds.map((id) => String(id)));
        setCopiedMeasurementIds((current) =>
          current.filter((id) => !deletedSet.has(String(id)))
        );
        setSelectedMeasurementIds((current) =>
          current.filter((id) => !deletedSet.has(String(id)))
        );
        setActiveMeasurementId((current) =>
          current && deletedSet.has(String(current)) ? null : current
        );
      }
      toast.success(deletedCount === 1 ? "Measurement deleted" : `${deletedCount} measurements deleted`);
    } catch (error) {
      console.error(error);
      toast.error("Failed to delete measurements");
    } finally {
      setIsDeletingMeasurementId(null);
    }
  };

  const handleReassignMeasurements = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    tagId: Id<"takeoffPilotExtractedTags">
  ) => {
    if (measurementIds.length === 0) {
      return false;
    }
    try {
      if (measurementIds.length === 1) {
        setIsReassigningMeasurementId(measurementIds[0]);
      }
      const result = await reassignMeasurements({ measurementIds, tagId });
      const updatedCount = result?.updatedCount ?? 0;
      if (updatedCount > 0) {
        setLastUsedMeasurementTagId(tagId);
      }
      toast.success(updatedCount === 1 ? "Measurement updated" : `${updatedCount} measurements updated`);
      return true;
    } catch (error) {
      console.error(error);
      toast.error(getMeasurementMutationErrorMessage(error, "Failed to update tag"));
      return false;
    } finally {
      setIsReassigningMeasurementId(null);
    }
  };

  const handleApplyMeasurementLeaders = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => {
    if (measurementIds.length === 0) {
      return false;
    }
    try {
      await applyMeasurementLeaders({
        measurementIds,
        leaderVisible,
      });
      return true;
    } catch (error) {
      console.error(error);
      toast.error(getMeasurementMutationErrorMessage(error, "Failed to update measurement leader"));
      return false;
    }
  };

  return (
    <div className="relative h-full overflow-hidden border bg-background shadow-sm">
      {/* Top Header */}
      <TakeoffViewerHeader
        viewerSources={viewerSources}
        selectedPlanDocumentId={selectedPlanDocumentId}
        onSelectPlanDocumentId={onSelectPlanDocumentId}
        onClose={onClose}
        onDownloadWorkbook={onDownloadWorkbook}
        onExportPdf={onExportPdf}
        onDownloadPdf={onDownloadPdf}
        exportStatus={exportStatus}
        exportError={exportError}
        isPdfExportInProgress={isPdfExportInProgress}
        canExportPdf={canExportPdf}
        canDownloadPdf={canDownloadPdf}
        canDownload={canDownload}
        tagsCount={tags.length}
        activePage={activePage}
        pageCount={pageCount}
        onGoToPage={(page) => viewportRef.current?.goToPage(page)}
        toolsets={
          !isViewOnly ? (
            <ViewportToolsetsHeader
              enabledToolsets={headerToolsets}
              controllers={railControllers}
            />
          ) : null
        }
      />

      <DocumentViewportHost
        source={source}
        activePage={activePage}
        onActivePageChange={setActivePage}
        onPageCountChange={setPageCount}
        pointerOwner={pointerOwner}
        enableWheelZoom={!measurementScale.isCalibrationMode}
        viewportPadding={{
          top: 80,
          right: 24,
          bottom: 28,
          left: 88,
        }}
        panBoundaryPadding={{ bottom: getBottomPanelPanPadding(bottomPanelHeight) }}
        className="absolute inset-0 overflow-hidden pt-14"
        toolbar={
          <TakeoffResultsViewportToolbar
            activePage={activePage}
            pageCount={pageCount}
            showOverlays={overlaysEnabled}
            overlaysAvailable={!isViewOnly}
            setShowOverlays={setShowOverlays}
          />
        }
        leftRail={!isViewOnly ? (
          <ViewportToolsetsRail
            enabledToolsets={railToolsets}
            controllers={railControllers}
          />
        ) : null}
      >
        <ViewportValueBinder onViewportChange={handleViewportChange} />
        {overlaysEnabled ? (
          <DocumentTagEditorOverlay
            revisionId={planRevisionId}
            activeDetections={activePageDetections}
            archivedDetections={archivedPageDetections}
            getStyle={(detection) => {
              const color = getDetectionColor(detection);
              return {
                color,
                fillColor: hexToRgba(color, 0.18),
              };
            }}
            panelClassName="top-20"
            highlightedDetectionId={highlightedDetectionId}
            editMode={activeToolsetId === "tags"}
            onEditModeChange={(value) => {
              if (value) {
                setActiveTagTool(TAG_TOOL_IDS.add);
                setActiveToolsetId("tags");
                return;
              }
              setActiveTagTool(null);
              setActiveToolsetId((current) => (current === "tags" ? null : current));
            }}
            showEditToggle={false}
            hidePanelWhenNotEditing
            showPanelOnlyWhenSelection
            allowCreate={false}
            tagOptions={tagPlacementOptions}
            selectedAddTagOptionId={selectedAddTagOptionId}
            placementMode={tagPlacementMode}
            onAction={recordActionLog}
          />
        ) : null}
        <CalibrationOverlay
          calibrationState={measurementScale.calibrationState}
          isCalibrationMode={measurementScale.isCalibrationMode}
          onViewportClick={measurementScale.handleViewportClick}
          onUpdatePoint1={measurementScale.updatePoint1}
          onUpdatePoint2={measurementScale.updatePoint2}
        />
        <MeasurementOverlay
          scale={measurementScale.scale}
          activeTool={activeTool}
          activeMeasurementType={activeMeasurementType}
          currentHeightFeet={currentHeightFeet}
          snapEnabled={snapEnabled}
          incrementSnapEnabled={incrementSnapEnabled}
          incrementStepFeet={incrementStepFeet}
          textLocked={textLocked}
          textFontSize={textFontSize}
          measurements={isViewOnly ? [] : (pageMeasurements as MeasurementOverlayRecord[])}
          colorByTagId={colorByTagId}
          labelByTagId={labelByTagId}
          draftMeasurement={draftMeasurement}
          draftTagId={draftMeasurement?.tagId ?? null}
          tags={measurementTags}
          onDraftMeasurementChange={handleDraftMeasurementChange}
          onDraftTagChange={(tagId: Id<"takeoffPilotExtractedTags">) =>
            setDraftMeasurement((current: DraftMeasurementState | null) => (current ? { ...current, tagId } : current))
          }
          onSaveDraftMeasurement={handleSaveDraftMeasurement}
          isSavingDraftMeasurement={isSavingDraftMeasurement}
          onCancelDraftMeasurement={() => setDraftMeasurement(null)}
          selectedMeasurementIds={selectedMeasurementIds}
          activeMeasurementId={activeMeasurementId}
          onSelectMeasurement={handleSelectMeasurement}
          onUpdateMeasurement={handleUpdateMeasurement}
          onUpdateMeasurementAnnotation={handleUpdateMeasurementAnnotation}
          onReassignMeasurement={handleReassignMeasurement}
          onReassignMeasurements={handleReassignMeasurements}
          isReassigningMeasurementId={isReassigningMeasurementId}
          isDeletingMeasurementId={isDeletingMeasurementId}
          onDeleteMeasurement={handleDeleteMeasurement}
          onDeleteMeasurements={handleDeleteMeasurements}
          onApplyMeasurementLeaders={handleApplyMeasurementLeaders}
          canPasteMeasurement={copiedMeasurementIds.length > 0}
          onCopyMeasurement={handleCopyMeasurement}
          onCopyMeasurements={handleCopyMeasurements}
          onPasteMeasurement={handlePasteMeasurement}
          onDuplicateMeasurement={handleDuplicateMeasurement}
          onDuplicateMeasurements={handleDuplicateMeasurements}
        />
      </DocumentViewportHost>

      {overlaysEnabled &&
      activePageDetections.length === 0 &&
      archivedPageDetections.length === 0 &&
      !isPending ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
          <div className="pointer-events-auto max-w-xl rounded-xl border bg-background/95 px-4 py-3 text-sm text-muted-foreground shadow-lg backdrop-blur">
            No indexed detections for the selected tags on this page.
          </div>
        </div>
      ) : null}
      {overlaysEnabled &&
      activePageDetections.length === 0 &&
      archivedPageDetections.length > 0 &&
      !isPending ? (
        <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 flex justify-center px-4">
          <div className="pointer-events-auto max-w-xl rounded-xl border bg-background/95 px-4 py-3 text-sm text-muted-foreground shadow-lg backdrop-blur">
            No active detections remain on this page. Hidden tags are still available to restore.
          </div>
        </div>
      ) : null}
    </div>
  );
});

TakeoffResultsViewer.displayName = "TakeoffResultsViewer";
